import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Download, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function FormSubmissions() {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [openSubmission, setOpenSubmission] = useState(null);

  const load = useCallback(async () => {
    try {
      const [fr, sr] = await Promise.all([
        api.get(`/forms/${id}`),
        api.get(`/forms/${id}/submissions`,
                { params: { page, page_size: PAGE_SIZE, search: activeSearch || undefined } }),
      ]);
      setForm(fr.data);
      setData(sr.data);
    } catch (e) {
      toast.error("Failed to load submissions");
    }
  }, [id, page, activeSearch]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await api.get(`/forms/${id}/submissions.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `form-${form?.title?.replace(/\s+/g, "_") || id}-submissions.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch { toast.error("Export failed"); }
  };

  const fields = (form?.definition?.sections || []).flatMap((s) => s.fields || []);
  const visibleCols = fields.slice(0, 5);     // first 5 to keep the table readable
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <div data-testid="form-submissions-page">
      <PageHeader
        title={form?.title || "Submissions"}
        subtitle={
          <Link to={`/forms/${id}/edit`} className="hover:underline inline-flex items-center gap-1">
            <ArrowLeft size={11} /> Back to form
          </Link>
        }
        action={
          <Button onClick={exportCsv} variant="outline" size="sm" data-testid="btn-export-csv">
            <Download size={13} className="mr-1.5" /> Export CSV
          </Button>
        }
      >
        <div className="text-sm text-slate-500 mt-1">{data.total} submission{data.total === 1 ? "" : "s"}</div>
      </PageHeader>

      <div className="px-4 sm:px-8 py-6">
        <div className="mb-4 flex items-center gap-2 max-w-md">
          <Search size={13} className="text-slate-400" />
          <Input
            placeholder="Search answers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), setActiveSearch(search))}
            data-testid="submissions-search"
          />
          <Button size="sm" variant="outline"
                  onClick={() => { setPage(1); setActiveSearch(search); }}>Apply</Button>
          {activeSearch && (
            <Button size="sm" variant="ghost"
                    onClick={() => { setSearch(""); setActiveSearch(""); setPage(1); }}>Clear</Button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          {data.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">
              No submissions yet. Share the public URL to start collecting responses.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wide text-slate-500">Submitted</th>
                  {visibleCols.map((f) => (
                    <th key={f.id}
                        className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wide text-slate-500 whitespace-nowrap">
                      {f.label}
                    </th>
                  ))}
                  {fields.length > visibleCols.length && (
                    <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wide text-slate-500">+{fields.length - visibleCols.length} more</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.id}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setOpenSubmission(s)}
                      data-testid={`submission-row-${s.id}`}>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">
                      {new Date(s.created_at).toLocaleString("en-IN", {
                        day: "2-digit", month: "short", year: "2-digit",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    {visibleCols.map((f) => (
                      <td key={f.id} className="px-3 py-2.5 max-w-xs truncate">
                        {renderCell(s.answers?.[f.key])}
                      </td>
                    ))}
                    {fields.length > visibleCols.length && <td className="text-slate-300">…</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft size={12} />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight size={12} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!openSubmission} onOpenChange={(v) => !v && setOpenSubmission(null)}>
        <DialogContent className="max-w-xl" data-testid="submission-detail-dialog">
          <DialogHeader><DialogTitle>Submission details</DialogTitle></DialogHeader>
          {openSubmission && (
            <div className="space-y-3 text-sm">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                Submitted {new Date(openSubmission.created_at).toLocaleString("en-IN")}
              </div>
              {fields.map((f) => (
                <div key={f.id} className="border-b border-slate-100 pb-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{f.label}</div>
                  <div className="text-sm mt-0.5 whitespace-pre-wrap">
                    {openSubmission.answers?.[f.key] != null
                      ? renderCell(openSubmission.answers[f.key])
                      : <span className="text-slate-300 italic">— skipped or empty —</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function renderCell(v) {
  if (v == null || v === "") return <span className="text-slate-300">—</span>;
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}
